/**
 * QR scanning, web implementation.
 *
 * Uses ZXing rather than the browser's `BarcodeDetector`, because the merchant
 * target is an iPad and Safari does not implement `BarcodeDetector`. ZXing reads
 * frames off a `<video>` element, which is created here with `createElement`
 * rather than through React Native — the camera is inherently a DOM concern and
 * pretending otherwise would only add a layer.
 *
 * Decoding happens in the browser, but the scanned token is only ever *verified*
 * on the server (`complete_order_by_token`), so a forged QR cannot redeem an
 * order no matter what this component reports.
 */

import { BrowserQRCodeReader, type IScannerControls } from '@zxing/browser';
import { TriangleAlert } from 'lucide-react-native';
import { createElement, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';

import { Stack } from './layout';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Text } from './ui/text';
import type { QrScannerProps } from '../lib/qr-types';

/**
 * A live camera viewfinder that reports decoded QR payloads.
 *
 * @param props Scan handler, activation, and de-duplication window.
 * @returns A video preview with status messaging.
 */
export function QrScanner({ onScan, active = true, paused, dedupeMs = 2500, onError }: QrScannerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const lastScanRef = useRef<{ text: string; at: number }>({ text: '', at: 0 });

  const [state, setState] = useState<'idle' | 'starting' | 'running' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [viewfinder, setViewfinder] = useState<{ width: number; height: number } | null>(null);

  // Handlers live in refs so that changing a prop never tears down the camera:
  // restarting a MediaStream on every render would flicker the viewfinder.
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  const pausedRef = useRef(Boolean(paused));
  onScanRef.current = onScan;
  onErrorRef.current = onError;
  pausedRef.current = Boolean(paused);

  useEffect(() => {
    if (!active) {
      setState('idle');
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;
    const reader = new BrowserQRCodeReader(undefined, {
      // Four attempts a second is responsive without pegging the CPU on an iPad
      // that is also driving the order queue.
      delayBetweenScanAttempts: 250,
    });

    setState('starting');
    setError(null);

    void (async () => {
      try {
        const controls = await reader.decodeFromVideoDevice(undefined, video, (result) => {
          if (cancelled || !result || pausedRef.current) return;

          const text = result.getText();
          const now = Date.now();
          // ZXing reports the same code on consecutive frames; without this the
          // merchant would fire one completion request per frame.
          if (lastScanRef.current.text === text && now - lastScanRef.current.at < dedupeMs) return;

          lastScanRef.current = { text, at: now };
          onScanRef.current(text);
        });

        if (cancelled) {
          controls.stop();
          return;
        }
        controlsRef.current = controls;
        setState('running');
      } catch (cause) {
        if (cancelled) return;
        const message = describeCameraError(cause);
        setError(message);
        setState('error');
        onErrorRef.current?.(message);
      }
    })();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
      controlsRef.current = null;
      // Explicitly release the track; on iPad Safari a stopped reader alone can
      // leave the camera indicator lit.
      const stream = video.srcObject as MediaStream | null;
      stream?.getTracks().forEach((track) => track.stop());
      video.srcObject = null;
    };
  }, [active, dedupeMs]);

  // 58% of the shorter side keeps the guide clear of all four edges in either
  // orientation, and keeps it the same apparent size as it had in portrait.
  const guide = viewfinder
    ? Math.round(Math.min(viewfinder.width, viewfinder.height) * 0.58)
    : null;

  return (
    <Stack gap="md">
      <View
        onLayout={(event) => setViewfinder(event.nativeEvent.layout)}
        className="bg-black relative aspect-[4/3] max-h-[460px] w-full items-center justify-center overflow-hidden rounded-xl">
        {/* Raw DOM element: getUserMedia needs a real HTMLVideoElement. */}
        {createElement('video', {
          ref: videoRef,
          autoPlay: true,
          muted: true,
          playsInline: true,
          style: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: state === 'running' ? 'block' : 'none',
          },
        })}

        {state !== 'running' ? (
          <View className="absolute items-center gap-3">
            {state === 'error' ? (
              <Text className="px-4 text-center text-white">{error}</Text>
            ) : (
              <Stack gap="sm" align="center">
                <ActivityIndicator size="large" className="text-primary" />
                <Text className="text-center text-xs font-medium text-white/70">
                  Starting camera…
                </Text>
              </Stack>
            )}
          </View>
        ) : null}

        {/* Framing guide: a square where the QR should sit.
            Sized from the shorter side rather than from the width: the frame is
            4:3 capped at 460px tall, so in landscape it is wide and short, and a
            square sized from its width overflows top and bottom. */}
        {state === 'running' && guide !== null ? (
          <View
            pointerEvents="none"
            style={{ width: guide, height: guide }}
            className="absolute rounded-xl border-[3px] border-white/85"
          />
        ) : null}
      </View>

      {state === 'error' ? (
        <Alert
          icon={TriangleAlert}
          className="border-warning/40 bg-warning-soft"
          iconClassName="text-warning">
          <AlertTitle>Camera unavailable</AlertTitle>
          <AlertDescription>
            Grant camera access in your browser settings, then reload. You can still complete this
            order by typing the customer's six-character code.
          </AlertDescription>
        </Alert>
      ) : (
        <Text className="text-muted-foreground text-center text-xs font-medium">
          Point the camera at the QR code on the customer's phone.
        </Text>
      )}
    </Stack>
  );
}

/**
 * Turns a camera failure into something a staff member can act on.
 *
 * @param cause Whatever `getUserMedia` or ZXing rejected with.
 * @returns A short, non-technical explanation.
 */
function describeCameraError(cause: unknown): string {
  const name = (cause as { name?: string })?.name ?? '';
  const message = cause instanceof Error ? cause.message : String(cause);

  // Checked first because it produces the vaguest failure of all: outside a
  // secure context the browser does not expose `navigator.mediaDevices` at all,
  // so the throw is a TypeError about reading a property of undefined and every
  // branch below misses it. This is also the common case in practice — a tablet
  // opening a dev server over the local network is on `http://<lan-ip>`, which
  // no browser treats as secure.
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'The camera needs a secure connection. Open this app over https, or on localhost.';
  }
  if (name === 'NotAllowedError' || /permission/i.test(message)) {
    return 'Camera permission was denied.';
  }
  if (name === 'NotFoundError' || /no.*camera|device not found/i.test(message)) {
    return 'No camera was found on this device.';
  }
  if (name === 'NotReadableError') {
    return 'The camera is already in use by another app.';
  }
  if (/insecure|https/i.test(message)) {
    return 'The camera needs a secure (https) connection.';
  }
  return 'The camera could not be started.';
}
